/// <amd-module name="Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { SuiteEntry, TestCase } from "TestPlanCommon/Contracts/TestPlan";
import { IListSelection } from "VSSUI/List";
import { SortOrder } from "VSSUI/Table";

export const AuthorActions = {
    // Action to initialize author pivot (like setup initial filter bar state etc.)
    initializeAuthorPivot: () => createAction(AuthorActionTypes.initializeAuthorPivot),

    // Action to invoke loading the initial testcases as a part of page load
    loadInitialTestCases: () => createAction(AuthorActionTypes.loadInitialTestCases),

    // Action to invoke when the initial testcases are fetched
    loadInitialTestCasesSucceeded: (testCases: TestCase[]) => createAction(AuthorActionTypes.loadInitialTestCasesSucceeded, testCases),

    // Action to invoke loading of testcases with a continuation token
    loadTestCasesForSelectedSuite: (testPlanId: number, testSuiteId: number, continuationToken?: string) =>
        createAction(AuthorActionTypes.loadTestCasesForSelectedSuite, { testPlanId, testSuiteId, continuationToken }),

    // Action to invoke unload of testcases.Invoked when the suite is changed.
    unloadPreviousTestCases: () => createAction(AuthorActionTypes.unloadPreviousTestCases),

    // Action to invoke when testcases are fetched
    loadTestCasesForSelectedSuiteSucceeded: (testCases: TestCase[], reload?: boolean) =>
        createAction(AuthorActionTypes.loadTestCasesForSelectedSuiteSucceeded, { testCases, reload }),

    // Action to invoke to open testcase via wit Form.
    openNewTestCaseWitForm: (testplanId: number, testSuiteId: number, areaPath: string, iterationPath: string) =>
        createAction(AuthorActionTypes.openNewTestCaseWitForm, { testplanId, testSuiteId, areaPath, iterationPath }),

    // Action to invoke adding of existing test cases
    addExistingTestCases: (planId: number, suiteId: number, areaPath: string) =>
        createAction(AuthorActionTypes.addExistingTestCases, { planId, suiteId, areaPath }),

    // Action to invoke adding or editing of test cases using grid.
    addOrEditTestCasesUsingGrid: (
        testCaseIds: number[],
        planId: number,
        suiteId: number,
        planName: string,
        planAreaPath: string,
        planIteration: string
    ) => createAction(AuthorActionTypes.addOrEditTestCasesUsingGrid, { testCaseIds, planId, suiteId, planName, planAreaPath, planIteration }),

    // Action to invoke to open existing testcase via wit form
    openExistingTestCaseWitForm: (
        planId: number,
        suiteId: number,
        testCaseId: number,
        testCaseTitle: string,
        testCaseState: string,
        testCaseAssignedTo: string
    ) =>
        createAction(AuthorActionTypes.openExistingTestCaseWitForm, {
            planId,
            suiteId,
            testCaseId,
            testCaseTitle,
            testCaseState,
            testCaseAssignedTo
        }),

    // Action to invoke to update table column entries when testcase is saved via wit form
    updateTestCase: (
        testCaseId: number,
        testCaseTitle: string,
        testCaseState: string,
        testCaseAssignedTo: string,
        columnOptions: { [id: string]: any }
    ) => createAction(AuthorActionTypes.updateTestCase, { testCaseId, testCaseTitle, testCaseState, testCaseAssignedTo, columnOptions }),

    // Action to invoke assign configuration to test case from author context menu
    assignConfigurationToTestCases: (planId: number, suiteId: number, testCaseIds: number[]) =>
        createAction(AuthorActionTypes.assignConfigurationToTestCases, { planId, suiteId, testCaseIds }),

    // Action to update filteredTestCases in store, the selector will use this to return test cases to render
    updateFilteredTestCases: (filteredTestCases: number[] | undefined) =>
        createAction(AuthorActionTypes.updateFilteredTestCases, { filteredTestCases }),

    // dispatched to get the test case object from server
    getTestCasesFromServer: (planId: number, suiteId: number, testCaseIds: number[]) =>
        createAction(AuthorActionTypes.getTestCasesFromServer, { planId, suiteId, testCaseIds }),

    // dispatched after test case object is successfully retrieved from server
    getTestCasesFromServerSucceeded: (testCases: TestCase[]) => createAction(AuthorActionTypes.getTestCasesFromServerSucceeded, { testCases }),

    // dispatched to remove one or more test cases from suite
    removeTestCasesFromSuite: (planId: number, suiteId: number, testCaseIds: number[], selection: IListSelection) =>
        createAction(AuthorActionTypes.removeTestCasesFromSuite, { planId, suiteId, testCaseIds, selection }),

    // dispatched after test case is successfully removed from suite
    removeTestCasesFromSuiteSucceeded: (planId: number, suiteId: number, testCaseIds: number[]) =>
        createAction(AuthorActionTypes.removeTestCasesFromSuiteSucceeded, { planId, suiteId, testCaseIds }),

    // Action to update column id and sort order for sorting of table items
    updateColumnAndSortOrder: (columnId: string, proposedSortOrder: SortOrder) =>
        createAction(AuthorActionTypes.updateColumnAndSortOrder, { columnId, proposedSortOrder }),

    // Action to change order of test cases in case of drag drop table items
    reorderTestCases: (planId: number, suiteId: number, draggedtestCaseIds: number[], startTestCaseOrder: number, shiftedTestCaseIds: number[]) =>
        createAction(AuthorActionTypes.reorderTestCases, { planId, suiteId, draggedtestCaseIds, startTestCaseOrder, shiftedTestCaseIds }),

    // dispatched to reorder test case table items in UI once the reorder api is successfull
    reorderTestCasesSucceeded: (suiteEntries: SuiteEntry[]) => createAction(AuthorActionTypes.reorderTestCasesSucceeded, { suiteEntries }),

    // dispatched to open dialog for bulk editing the test cases
    bulkEditTestCases: (planId: number, suiteId: number, testCaseIds: number[]) =>
        createAction(AuthorActionTypes.bulkEditTestCases, { planId, suiteId, testCaseIds }),

    // dispatched to save column widths via settings service
    saveColumnWidths: (columnAndWidthMap: { [columnId: string]: number }) => createAction(AuthorActionTypes.saveColumnWidths, { columnAndWidthMap }),

    // disptached to fetch column widths from setting service
    fetchColumnWidths: () => createAction(AuthorActionTypes.fetchColumnWidths, {}),

    /**
     * getTestCasesCloneOpState: Get state of testcase clone operation.
     */
    getTestCasesCloneOpState: (
        sourcePlanId: number,
        sourceSuiteId: number,
        destinationPlanId: number,
        destinationSuiteId: number,
        destinationProject: string,
        opId: number,
        initialCall?: boolean,
        project?: string
    ) =>
        createAction(AuthorActionTypes.getTestCasesCloneOpState, {
            sourcePlanId,
            sourceSuiteId,
            destinationPlanId,
            destinationSuiteId,
            destinationProject,
            opId,
            initialCall,
            project
        }),

    openSuiteInNewWindow: (project: string, planId: number, suiteId: number) =>
        createAction(AuthorActionTypes.openSuiteInNewWindow, { project, planId, suiteId }),

    //dispatched to filter test cases in state
    filterTestCasesInState: () => createAction(AuthorActionTypes.filterTestCasesInState, {}),

    //dispatched to fetch test cases count for suite tree
    fetchSuiteTestCasesCount: () => createAction(AuthorActionTypes.fetchSuiteTestCasesCount, {})
};

export const enum AuthorActionTypes {
    initializeAuthorPivot = "AuthorPivot/initializeAuthorPivot",
    loadInitialTestCases = "AuthorPivot/loadInitialTestCases",
    loadInitialTestCasesSucceeded = "AuthorPivot/loadInitialTestCasesSucceeded",
    loadTestCasesForSelectedSuite = "AuthorPivot/loadTestCasesForSelectedSuite",
    loadTestCasesForSelectedSuiteSucceeded = "AuthorPivot/loadTestCasesForSelectedSuiteSucceeded",
    openNewTestCaseWitForm = "AuthorPivot/openNewTestCaseWitForm",
    unloadPreviousTestCases = "AuthorPivot/unloadPreviousTestCases",
    addExistingTestCases = "AuthorPivot/addExistingTestCases",
    addOrEditTestCasesUsingGrid = "AuthorPivot/addOrEditTestCasesUsingGrid",
    openExistingTestCaseWitForm = "AuthorPivot/openExistingTestCaseWitForm",
    updateTestCase = "AuthorPivot/updateTestCase",
    assignConfigurationToTestCases = "AuthorPivot/assignConfigurationToTestCases",
    updateFilteredTestCases = "AuthorPivot/updateFilteredTestCases",
    filterTestCasesInState = "AuthorPivot/filterTestCasesInState",
    fetchSuiteTestCasesCount = "AuthorPivot/fetchSuiteTestCasesCount",
    getTestCasesFromServer = "AuthorPivot/getTestCasesFromServer",
    getTestCasesFromServerSucceeded = "AuthorPivot/getTestCasesFromServerSucceeded",
    removeTestCasesFromSuite = "AuthorPivot/removeTestCasesFromSuite",
    removeTestCasesFromSuiteSucceeded = "AuthorPivot/removeTestCasesFromSuiteSucceeded",
    updateColumnAndSortOrder = "AuthorPivot/updateColumnAndSortOrder",
    reorderTestCases = "AuthorPivot/reorderTestCases",
    reorderTestCasesSucceeded = "AuthorPivot/reorderTestCasesSucceeded",
    bulkEditTestCases = "AuthorPivot/bulkEditTestCases",
    saveColumnWidths = "AuthorPivot/saveColumnWidths",
    fetchColumnWidths = "AuthorPivot/fetchColumnWidths",
    getTestCasesCloneOpState = "AuthorPivot/getTestCasesCloneOpState",
    openSuiteInNewWindow = "AuthorPivot/openSuiteInNewWindow"
}

export type AuthorActions = ActionsUnion<typeof AuthorActions>;
